import {
  useCallback,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  clamp,
  fineStep,
  normFromValue,
  quantize,
  valueFromNorm,
  type ValueScale,
} from "./math.js";

export type KnobProps = {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  label: string;
  format?: (value: number) => string;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  /** Display/drag mapping; emitted values stay in real units (e.g. Hz). */
  scale?: ValueScale;
};

const START_ANGLE = -135;
const SWEEP = 270;
const DRAG_PIXELS = 120;

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const start = polar(cx, cy, r, endDeg);
  const end = polar(cx, cy, r, startDeg);
  const large = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y}`;
}

export function Knob({
  value,
  min = 0,
  max = 1,
  step = 0.01,
  defaultValue,
  label,
  format = (v) => String(Math.round(v * 1000) / 1000),
  onChange,
  disabled = false,
  className,
  scale = "linear",
}: KnobProps) {
  const id = useId();
  const labelId = `${id}-label`;
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startValue: number; fine: boolean } | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const commit = useCallback(
    (next: number) => {
      onChange(quantize(next, min, max, step));
    },
    [max, min, onChange, step],
  );

  const reset = useCallback(() => {
    if (defaultValue !== undefined) commit(defaultValue);
  }, [commit, defaultValue]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || editing) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      startY: event.clientY,
      startValue: value,
      fine: event.shiftKey,
    };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const fine = drag.fine || event.shiftKey;
    const delta = (drag.startY - event.clientY) / DRAG_PIXELS;
    const sensitivity = fine ? 0.1 : 1;
    const startNorm = normFromValue(drag.startValue, min, max, scale);
    const nextNorm = clamp(startNorm + delta * sensitivity, 0, 1);
    commit(valueFromNorm(nextNorm, min, max, step, scale));
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current) {
      event.currentTarget.releasePointerCapture(event.pointerId);
      dragRef.current = null;
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const useFine = event.shiftKey;
    const unit = useFine ? fineStep(step, min, max) : step;
    const page = unit * 10;

    switch (event.key) {
      case "ArrowUp":
      case "ArrowRight":
        event.preventDefault();
        commit(value + unit);
        break;
      case "ArrowDown":
      case "ArrowLeft":
        event.preventDefault();
        commit(value - unit);
        break;
      case "PageUp":
        event.preventDefault();
        commit(value + page);
        break;
      case "PageDown":
        event.preventDefault();
        commit(value - page);
        break;
      case "Home":
        event.preventDefault();
        commit(min);
        break;
      case "End":
        event.preventDefault();
        commit(max);
        break;
      default:
        break;
    }
  };

  const startEdit = () => {
    if (disabled) return;
    setDraft(format(value));
    setEditing(true);
  };

  const finishEdit = () => {
    const parsed = Number.parseFloat(draft);
    if (Number.isFinite(parsed)) commit(parsed);
    setEditing(false);
  };

  const norm = normFromValue(value, min, max, scale);
  const endAngle = START_ANGLE + SWEEP * norm;
  const valueText = format(value);
  const size = 72;
  const cx = size / 2;
  const cy = size / 2;
  const r = 26;

  return (
    <div className={["inline-flex flex-col items-center gap-1", className].filter(Boolean).join(" ")}>
      <div id={labelId} className="text-[11px] uppercase tracking-[0.08em] text-ink-muted">
        {label}
      </div>
      <div
        ref={rootRef}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-labelledby={labelId}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={valueText}
        aria-disabled={disabled || undefined}
        className="relative touch-none select-none rounded-[var(--radius-control)] outline-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={reset}
        onKeyDown={onKeyDown}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <path
            d={describeArc(cx, cy, r, START_ANGLE, START_ANGLE + SWEEP)}
            fill="none"
            stroke="var(--color-hairline)"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d={describeArc(cx, cy, r, START_ANGLE, endAngle)}
            fill="none"
            stroke="var(--color-signal)"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx={cx} cy={cy} r="18" fill="var(--color-panel)" stroke="var(--color-hairline)" />
          <line
            x1={cx}
            y1={cy}
            x2={polar(cx, cy, 14, endAngle).x}
            y2={polar(cx, cy, 14, endAngle).y}
            stroke="var(--color-ink)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      {editing ? (
        <input
          className="readout w-16 rounded-[var(--radius-control)] border border-hairline bg-panel-hi px-1 py-0.5 text-center text-xs text-ink"
          value={draft}
          autoFocus
          aria-label={`${label} value`}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={finishEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") finishEdit();
            if (e.key === "Escape") setEditing(false);
          }}
        />
      ) : (
        <button
          type="button"
          className="readout text-xs text-ink-muted hover:text-ink"
          onClick={startEdit}
          disabled={disabled}
        >
          {valueText}
        </button>
      )}
    </div>
  );
}

export function knobValueAtNorm(norm: number, min: number, max: number, step: number): number {
  return valueFromNorm(clamp(norm, 0, 1), min, max, step);
}
